import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useState,
} from "react";
import styles from "./AssetCard.module.scss";
import { CompositeAsset } from "../../../../common/api/decimal/common-types/manageAssets.ts";
import EmptyAssetCard from "../../../../common/features/asset/EmptyAssetCard.tsx";
import useExploreFetcher from "../../actions/explore/useExploreFetcher.tsx";
import DefaultCoverBadge from "../../../../common/features/asset/DefaultCoverBadge.tsx";
import AssetTopRow from "../../../../common/features/asset/AssetTopRow.tsx";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import TruncatedTitle from "../../../../common/features/asset/TruncatedTitle.tsx";
import NthEdition from "../../../../common/features/asset/NthEdition.tsx";
import AssetFeatureTags from "../../../../common/features/asset/AssetFeatureTags.tsx";
import useStandardAssetPopover from "../asset-popover/useStandardAssetPopover.tsx";

interface AssetCardProps {
  asset: CompositeAsset;
  lazyLoad?: boolean;
  type?: ComponentPropsWithoutRef<typeof EmptyAssetCard>["type"];
  hideOnReturn: boolean;
  showContentSearch?: boolean;
  contentSearchSection?: string;
}

const AssetCard = forwardRef<HTMLElement, AssetCardProps>(function AssetCard(
  {
    lazyLoad,
    asset,
    type = "auto",
    hideOnReturn = true,
    showContentSearch,
    contentSearchSection,
  },
  ref
) {
  const { returnComplete } = useExploreFetcher(asset.explore);
  const [isHidden, setIsHidden] = useState(false);

  /**
   * It is possible for fetchers to disappear prior to revalidation completing
   * if you do a second return while the first return is revalidating.
   * I don't entirely understand what's going on, but let's cache the successful
   * return locally so books don't reappear.
   * unless we don't want the book to disappear, e.g. favorites
   */
  useEffect(() => {
    if (returnComplete && hideOnReturn) {
      setIsHidden(true);
    }
  }, [hideOnReturn, returnComplete]);

  const { popoverLinks, renderPopoverLinkDependencies, popoverActions } =
    useStandardAssetPopover(asset, { showContentSearch, contentSearchSection });

  if (isHidden) {
    return null;
  }

  return (
    <li>
      <EmptyAssetCard
        tagName="div"
        type={type}
        ref={ref}
        lazyLoad={lazyLoad}
        vbid={asset.manageAsset.vbid}
        primaryAction={popoverLinks[0]}
        imageChildren={
          <DefaultCoverBadge license={asset.license} explore={asset.explore} />
        }
        contentAreaClassName={styles.contentArea}
      >
        <AssetTopRow
          asset={asset.manageAsset}
          license={asset.license}
          explore={asset.explore}
          links={popoverLinks}
        />
        <BaseLinkOrButton
          onClick={popoverLinks[0]?.onClick}
          href={popoverLinks[0]?.href}
          to={popoverLinks[0]?.to}
          className={styles.title}
        >
          <TruncatedTitle asset={asset.manageAsset} />
        </BaseLinkOrButton>
        <NthEdition asset={asset.manageAsset} className={styles.edition} />
        <AssetFeatureTags
          asset={asset.manageAsset}
          license={asset.license}
          className={styles.featureTags}
          actions={{
            openAncillariesPanel: popoverActions.openAncillariesPanel,
            openAccessibilityPanel: popoverActions.openAccessibilityPanel,
          }}
        />
      </EmptyAssetCard>
      {renderPopoverLinkDependencies()}
    </li>
  );
});

export default AssetCard;
